import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, track } from "../lib/api";
import { Section, Reveal, Overline, Button } from "../components/primitives";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";

export function Journal() {
  const [posts, setPosts] = useState([]);
  const [email, setEmail] = useState("");
  useEffect(() => { api.get("/journal").then((r) => setPosts(r.data)).catch(() => {}); }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try { await api.post("/leads", { enquiry_type: "newsletter", full_name: "Subscriber", email }); track("newsletter_signup", {}); toast.success("Subscribed!"); setEmail(""); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div>
      <Seo title="The Cultiva Journal" description="Botanical education, product guides, recipes and wellness content from Cultiva Botanics — understanding moringa, turmeric, hibiscus and more." />
      <PageHero chapter="Journal" overline="The Cultiva Journal" title="Botanical notes & guides." subtitle="Education, product guides and recipes exploring the world of botanical wellness." />
      <Section className="py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <Link to={`/journal/${p.slug}`} className="group block" data-testid={`journal-${p.slug}`}>
                <div className="aspect-[4/3] overflow-hidden rounded-md"><img src={p.cover} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="text-[11px] uppercase tracking-widest text-turmeric mt-5">{p.category}</div>
                <h3 className="font-serif text-2xl text-botanical mt-2 group-hover:opacity-70 transition-opacity">{p.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{p.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>
      <Section className="py-20 bg-botanical text-ivory rounded-none grain">
        <div className="relative z-10 max-w-2xl">
          <Overline className="text-ivory/50">Newsletter</Overline>
          <h2 className="display text-4xl md:text-5xl mt-3">Stay in the loop.</h2>
          <p className="text-ivory/70 font-light mt-4">Botanical guides, new products and business updates — occasionally, never spam.</p>
          <form onSubmit={subscribe} className="mt-8 flex gap-3 max-w-md" data-testid="newsletter-form">
            <input required type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 bg-transparent border-b border-ivory/30 focus:border-ivory outline-none py-2.5 text-ivory placeholder:text-ivory/40" data-testid="newsletter-email" />
            <Button type="submit" variant="light" testId="newsletter-submit">Subscribe</Button>
          </form>
        </div>
      </Section>
    </div>
  );
}

export function JournalPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/journal/${slug}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [slug]);
  if (p === false) return <Section className="py-40 text-center"><h1 className="display text-4xl text-botanical">Article not found</h1><div className="mt-8 flex justify-center"><Button to="/journal">Back to Journal</Button></div></Section>;
  if (!p) return <div className="py-40 text-center text-muted-foreground">Loading…</div>;
  return (
    <div>
      <Seo title={p.title} description={p.excerpt} />
      <Section className="pt-16 pb-10 max-w-3xl">
        <Link to="/journal" className="text-sm text-muted-foreground link-underline">← The Journal</Link>
        <div className="text-[11px] uppercase tracking-widest text-turmeric mt-8">{p.category}</div>
        <h1 className="display text-5xl md:text-6xl text-botanical mt-3">{p.title}</h1>
      </Section>
      <Section className="pb-10 max-w-4xl"><img src={p.cover} alt={p.title} className="w-full aspect-[16/9] object-cover rounded-md" /></Section>
      <Section className="pb-24 max-w-3xl"><p className="text-lg text-muted-foreground font-light leading-relaxed whitespace-pre-line">{p.body}</p></Section>
    </div>
  );
}
